export const px = (value: string) => Number.parseFloat(value) || 0;

/** How much a transform (a dialog zooming in, say) scales `reference` on screen. */
export function screenScale(reference: HTMLElement) {
  const layout = reference.offsetWidth;
  return layout > 0 ? reference.getBoundingClientRect().width / layout || 1 : 1;
}

/** Horizontal padding plus border: what a box takes from its own content. */
export function horizontalChrome(style: CSSStyleDeclaration) {
  return (
    px(style.paddingLeft) +
    px(style.paddingRight) +
    px(style.borderLeftWidth) +
    px(style.borderRightWidth)
  );
}

const horizontalMargins = (style: CSSStyleDeclaration) =>
  px(style.marginLeft) + px(style.marginRight);

function isRow(style: CSSStyleDeclaration) {
  return (
    (style.display === "flex" || style.display === "inline-flex") &&
    !style.flexDirection.startsWith("column")
  );
}

function inFlow(element: Element) {
  const style = getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.position !== "absolute" &&
    style.position !== "fixed"
  );
}

/**
 * Everything on the label's line inside `container` that is not the label's
 * text: padding and borders of each box from the label up to the container,
 * and in each flex row the gaps and the other items (a swatch, an avatar, the
 * chevron).
 *
 * Contract: those other items must be fixed width or `shrink-0`. Their width
 * must not depend on the label's, or a cut would change the room it was cut
 * to fit. Bare text beside the label in a flex row is not counted; wrap it.
 */
export function widthAroundLabel(
  label: HTMLElement,
  container: HTMLElement
): number | undefined {
  if (!container.contains(label)) return undefined;
  const scale = screenScale(container);
  let total = horizontalChrome(getComputedStyle(label));
  let child: HTMLElement = label;
  while (child !== container) {
    const parent: HTMLElement | null = child.parentElement;
    if (!parent) return undefined;
    total += horizontalMargins(getComputedStyle(child));
    const style = getComputedStyle(parent);
    total += horizontalChrome(style);
    if (isRow(style)) {
      const items = [...parent.children].filter((item) => inFlow(item));
      total += px(style.columnGap) * Math.max(0, items.length - 1);
      for (const item of items) {
        if (item === child) continue;
        total +=
          item.getBoundingClientRect().width / scale +
          horizontalMargins(getComputedStyle(item));
      }
    }
    child = parent;
  }
  return total;
}
